/* Shared product and cart editor behavior. */
(() => {
  const customilyOptionWithFunction = (type) => window.HanossCustomilyCapabilities?.findOptions(type)?.[0] || null;
  const matchesCartEditLocation = (intent, current) => {
    if (!intent || intent.path !== current.pathname) return false;
    const saved = new URL(intent.path + intent.search, location.origin);
    const actual = new URL(current.pathname + current.search, location.origin);
    // Shopify's preview selector changes the theme, not the saved personalization.
    for (const url of [saved, actual]) { url.searchParams.delete('preview_theme_id'); url.searchParams.sort(); }
    return saved.search === actual.search;
  };
  const customilyControlWithType = (type) => window.HanossCustomilyCapabilities?.findControls(type) || [];
  const configuredFields = () => window.HanossCustomilyCapabilities?.getConfiguredFields?.() || {};
  const quoteOption = () => configuredFields().quoteOption || null;
  const quoteInput = () => configuredFields().quote || null;
  const crosswordOptionElement = () => configuredFields().crosswordOption || null;
  const crosswordInput = () => configuredFields().crossword || null;
  const relationshipOption = () => configuredFields().relationshipOption || null;
  const relationshipInput = () => configuredFields().relationship || null;
  const customTextOption = () => configuredFields().customTextOption || null;
  const customTextInput = () => configuredFields().customText || null;
  const propertyKey = (control) => {
    const match = control?.name?.match(/^properties\[(.*)]$/);
    return match?.[1] || '';
  };
  const optionModel = (option) => window.HanossCustomilyCapabilities?.optionModel?.(option) || null;
  const quoteValue = () => {
    const inputValue = quoteInput()?.value?.trim();
    if (inputValue) return inputValue;
    const displayed = quoteOption()?.querySelector('[role="combobox"]')?.textContent?.trim();
    if (!displayed) return '';
    const selected = (optionModel(quoteOption())?.values || []).find((value) =>
      value?.value === displayed || value?.text === displayed);
    return selected?.value || '';
  };
  const loadStoredCustomization = async (intent) => {
    let id = '';
    try { id = new URL(intent?.path + intent?.search, location.origin).searchParams.get('customizationId') || ''; } catch {}
    if (!id) return null;
    const deadline = Date.now() + 8000;
    while (typeof window.engraver?.getSharedCustomization !== 'function' && Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    if (typeof window.engraver?.getSharedCustomization !== 'function') return null;
    try {
      const response = await Promise.race([
        window.engraver.getSharedCustomization(id),
        new Promise((resolve) => setTimeout(() => resolve(null), 8000)),
      ]);
      const data = typeof response?.CustomizationData === 'string'
        ? JSON.parse(response.CustomizationData) : response?.CustomizationData;
      return data && typeof data === 'object' ? data : null;
    } catch { return null; }
  };
  const setupCartEditPage = () => {
    let intent;
    try { intent = JSON.parse(sessionStorage.getItem('hn-hng04-cart-edit-intent') || 'null'); } catch { return; }
    const currentUrl = new URL(location.href);
    if (!intent || intent.expires < Date.now() || !matchesCartEditLocation(intent, currentUrl)) return;
    const section = document.querySelector('.product-information__grid')?.closest('.shopify-section');
    if (!section) return;
    const referenceProduct = document.body.classList.contains('hn-ui-reference');
    document.body.classList.add('hn-ui-reference', 'hn-ui-cart-editor', 'hn-ui-cart');
    let cartFieldsResolved = false;
    let restoredProperties = {};
    const currentProperties = () => Object.fromEntries([...document.querySelectorAll('.customily_sets-panel [name^="properties["]')]
      .filter((input) => !['radio', 'checkbox'].includes(input.type) || input.checked)
      .map((input) => [propertyKey(input), input.value]));
    document.addEventListener('hn-ui-cart-edit-fields-resolved', (event) => {
      if (!event.detail || typeof event.detail !== 'object') return;
      cartFieldsResolved = true;
      restoredProperties = currentProperties();
      for (const key of ['quote', 'relationship', 'names', 'customText']) {
        if (typeof event.detail[key] === 'string') intent[key] = event.detail[key];
      }
    });
    const media = section.querySelector('.product-information__media');
    const gallery = media?.querySelector('media-gallery');
    const savedPreviewSources = [
      intent.previewImage,
      intent.properties?.['_customily-preview'],
      intent.properties?.['_customily-thumb'],
      intent.properties?.['_personalization_preview'],
    ].filter((value, index, values) => typeof value === 'string' && value.trim() && values.indexOf(value) === index);
    if (gallery && savedPreviewSources.length) {
      const savedPreview = document.createElement('img');
      savedPreview.className = 'hn-ui-cart-editor__saved-preview';
      savedPreview.alt = 'Saved personalized product preview';
      savedPreview.decoding = 'async';
      let sourceIndex = 0;
      document.body.classList.add('hn-ui-cart-editor__saved-preview-pending');
      let previewAvailable = true;
      let previewCurrent = true;
      let previewObserver;
      const clearSavedPreview = () => {
        previewCurrent = false;
        document.body.classList.remove('hn-ui-cart-editor__saved-preview-ready', 'hn-ui-cart-editor__saved-preview-pending');
        savedPreview.remove();
        previewObserver?.disconnect();
      };
      const showNextSource = () => {
        if (sourceIndex >= savedPreviewSources.length) {
          previewAvailable = false;
          clearSavedPreview();
          return;
        }
        let source;
        try {
          const candidate = new URL(savedPreviewSources[sourceIndex++], location.href);
          if (!['http:', 'https:'].includes(candidate.protocol)) return showNextSource();
          source = candidate.href;
        } catch {
          return showNextSource();
        }
        savedPreview.src = source;
      };
      savedPreview.addEventListener('load', () => {
        if (!previewCurrent) return;
        document.body.classList.remove('hn-ui-cart-editor__saved-preview-pending');
        document.body.classList.add('hn-ui-cart-editor__saved-preview-ready');
      });
      savedPreview.addEventListener('error', showNextSource);
      gallery.append(savedPreview);
      showNextSource();
      previewObserver = new MutationObserver(() => {
        if (!previewAvailable || !previewCurrent) return;
        if (!document.body.classList.contains('hn-ui-cart-editor')) { previewObserver.disconnect(); return; }
        const currentGallery = section.querySelector('.product-information__media media-gallery');
        if (currentGallery && savedPreview.parentElement !== currentGallery) currentGallery.append(savedPreview);
      });
      previewObserver.observe(section, { childList: true, subtree: true });
      document.addEventListener('hn-ui-cart-editor-prepare-artwork', clearSavedPreview, { once: true });
      for (const eventName of ['input', 'change', 'click']) {
        document.addEventListener(eventName, (event) => {
          if ((!cartFieldsResolved && !event.isTrusted)
            || !event.target?.closest?.('.customily_sets-panel, .hn-ui-name-tags')) return;
          requestAnimationFrame(() => {
            const changed = quoteValue() !== intent.quote
              || (relationshipInput()?.value || '') !== (intent.relationship || '')
              || (crosswordInput()?.value || '') !== (intent.names || '')
              || (customTextInput()?.value || '') !== (intent.customText || '');
            if (changed) clearSavedPreview();
          });
        }, { capture: true });
      }
    }
    const syncImageState = () => {
      if (!document.body.classList.contains('hn-ui-cart-editor')) return;
      const unverified = !document.body.classList.contains('hn-ui-render-current');
      const actions = section.querySelector('.hn-ui-cart-editor__actions');
      if (actions) {
        const loading = cartFieldsResolved && actions.querySelector('.hn-ui-cart-editor__error')?.hidden
          && document.body.classList.contains('hn-ui-gallery-loading');
        actions.classList.toggle('is-rendering', loading);
        const busy = loading || actions.classList.contains('is-busy');
        const canSync = cartFieldsResolved && !actions.classList.contains('is-busy')
          && actions.querySelector('.hn-ui-cart-editor__error')?.hidden;
        for (const button of actions.querySelectorAll('button')) {
          button.setAttribute('aria-busy', String(busy));
          if (canSync) button.disabled = loading;
        }
      }
      if (document.body.classList.contains('hn-ui-cart-editor__image-unverified') !== unverified) {
        document.body.classList.toggle('hn-ui-cart-editor__image-unverified', unverified);
      }
      if (unverified) media?.setAttribute('aria-busy', 'true');
      else media?.removeAttribute('aria-busy');
    };
    syncImageState();
    new MutationObserver(syncImageState).observe(document.body, { attributes: true, attributeFilter: ['class'] });
    try {
      const cartMarkup = sessionStorage.getItem('hn-hng04-cart-edit-background');
      // Old cached HTML uses the previous CSS/JS namespace; use the normal cart fallback.
      if (cartMarkup && !cartMarkup.includes('hn-hng04')) {
        const background = document.createElement('div');
        background.className = 'hn-ui-cart-editor__background';
        background.setAttribute('aria-hidden', 'true');
        background.innerHTML = cartMarkup;
        document.body.prepend(background);
      }
    } catch {}
    const heading = document.createElement('div');
    heading.className = 'hn-ui-cart-editor__heading';
    heading.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="m4 16.9-.9 4 4-.9L19.6 7.5a2.1 2.1 0 0 0-3-3L4 16.9Z"/></svg><h1>Edit Item</h1>';
    const close = document.createElement('a');
    close.className = 'hn-ui-cart-editor__close';
    close.href = '/cart';
    close.setAttribute('aria-label', 'Close item editor and return to cart');
    close.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true"><path d="M4 4l16 16M20 4L4 20" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"/></svg>';
    const restoreCart = (updated) => {
      let saved = updated;
      try { if (!saved) saved = JSON.parse(sessionStorage.getItem('hn-hng04-cart-return') || 'null'); } catch { return false; }
      if (saved?.main?.includes('hn-hng04')) return false;
      if (!saved || Date.now() - saved.capturedAt > 60 * 60 * 1000 || typeof saved.main !== 'string') return false;
      const parsed = new DOMParser().parseFromString(saved.main, 'text/html');
      const restoredMain = parsed.querySelector('#MainContent');
      const currentMain = document.querySelector('#MainContent');
      const restoredFooter = new DOMParser().parseFromString(saved.footer || '', 'text/html').querySelector('footer');
      const currentFooter = document.querySelector('.page-wrapper > footer');
      if (!restoredMain?.querySelector('.cart-page') || !currentMain || !restoredFooter || !currentFooter || !saved.cartSignature) return false;
      currentMain.replaceWith(document.importNode(restoredMain, true));
      currentFooter.replaceWith(document.importNode(restoredFooter, true));
      document.querySelector('.hn-ui-cart-editor__background')?.remove();
      document.querySelectorAll('.customily-modal-preview-only, .hn-mobile-floating-preview').forEach((overlay) => overlay.remove());
      document.body.className = saved.bodyClass || '';
      document.documentElement.className = saved.htmlClass || '';
      document.title = saved.title || document.title;
      history.replaceState(null, '', '/cart');
      const loadScript = (source, type) => {
        if (!source) return;
        const url = new URL(source, location.href);
        if (url.origin !== location.origin || !url.pathname.includes('/assets/')) return;
        if ([...document.head.querySelectorAll('script[src]')].some((existing) => existing.src === url.href)) return;
        const script = document.createElement('script');
        script.src = url.href;
        if (type) script.type = type;
        document.head.append(script);
      };
      for (const script of saved.scripts || []) loadScript(script.src, script.type);
      if (window.HanossInitCart) window.HanossInitCart();
      else loadScript(saved.cartScript);
      sessionStorage.removeItem('hn-hng04-cart-return');
      window.scrollTo(0, Number(saved.scrollY) || 0);
      [...document.querySelectorAll('[data-edit-line-key]')]
        .find((button) => button.dataset.editLineKey === intent.lineKey)?.focus({ preventScroll: true });
      fetch('/cart.js').then((response) => response.ok ? response.json() : null).then((cart) => {
        if (!cart) return;
        const currentSignature = JSON.stringify(cart.items.map((item) => [item.key, item.quantity]));
        if (currentSignature !== saved.cartSignature && location.pathname === '/cart') location.replace('/cart');
      }).catch(() => {});
      return true;
    };
    let completingSave = null;
    let saveCommitted = false;
    let replacementVerified = false;
    const returnUpdatedCart = async (cart) => {
      const response = await fetch('/cart', { signal: AbortSignal.timeout(15000), cache: 'no-store' });
      if (!response.ok) throw new Error('Cart view unavailable');
      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const saved = JSON.parse(sessionStorage.getItem('hn-hng04-cart-return') || 'null');
      const main = page.querySelector('#MainContent');
      const footer = page.querySelector('.page-wrapper > footer');
      if (!saved || !main?.querySelector('.cart-page') || !footer) throw new Error('Cart view unavailable');
      if (!window.HanossInitCart) {
        const source = [...page.scripts].find((script) => script.src.includes('/hn-ui-cart.js'))?.src;
        const url = new URL(source || '', location.href);
        const themeAsset = /\/(?:cdn\/shop\/t\/\d+|s\/files\/[^?]+\/t\/\d+)\/assets\/hn-ui-cart\.js$/.test(url.pathname);
        if (!source || !themeAsset || (url.origin !== location.origin && url.hostname !== 'cdn.shopify.com')) throw new Error('Cart controls unavailable');
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          const timer = setTimeout(() => { script.remove(); reject(new Error('Cart controls unavailable')); }, 15000);
          script.src = url.href;
          script.onload = () => { clearTimeout(timer); resolve(); };
          script.onerror = () => { clearTimeout(timer); script.remove(); reject(new Error('Cart controls unavailable')); };
          document.head.append(script);
        });
        if (!window.HanossInitCart) throw new Error('Cart controls unavailable');
      }
      const updated = { ...saved, capturedAt: Date.now(), main: main.outerHTML, footer: footer.outerHTML,
        title: page.title, cartSignature: JSON.stringify(cart.items.map((item) => [item.key, item.quantity])) };
      document.dispatchEvent(new Event('hn-ui-cart-editor-closed'));
      if (!restoreCart(updated)) throw new Error('Cart view unavailable');
      delete document.body.dataset.hnProductHandle;
      for (const count of document.querySelectorAll('.cart-bubble__text-count')) count.textContent = String(cart.item_count ?? cart.items.reduce((sum, item) => sum + item.quantity, 0));
      sessionStorage.removeItem('hn-hng04-cart-edit-intent');
      sessionStorage.removeItem('hn-hng04-cart-edit-background');
      window.HanossCompleteCartEdit = null;
    };
    window.HanossCompleteCartEdit = () => {
      if (!sessionStorage.getItem('hn-hng04-cart-legacy-replace') && !saveCommitted) return null;
      saveCommitted = true;
      if (!completingSave) completingSave = (async () => {
        const replaced = await window.HanossFinalizeCartEdit();
        if (replaced) replacementVerified = true;
        if (!replacementVerified) throw new Error('Cart replacement not verified');
        const cart = replaced
          || await fetch('/cart.js', { signal: AbortSignal.timeout(15000) }).then((r) => { if (!r.ok) throw new Error('Cart unavailable'); return r.json(); });
        await returnUpdatedCart(cart);
      })().catch((error) => {
        document.dispatchEvent(new CustomEvent('hn-ui-cart-save-error', { detail: error }));
        throw error;
      }).finally(() => { completingSave = null; });
      return completingSave;
    };
    close.addEventListener('click', (event) => {
      event.preventDefault();
      if (close.getAttribute('aria-disabled') === 'true') return;
      if (saveCommitted) { location.assign('/cart'); return; }
      document.dispatchEvent(new Event('hn-ui-cart-editor-closed'));
      sessionStorage.removeItem('hn-hng04-cart-edit-intent');
      sessionStorage.removeItem('hn-hng04-cart-legacy-replace');
      sessionStorage.removeItem('hn-hng04-cart-add-new-restore');
      sessionStorage.removeItem('hn-hng04-cart-edit-background');
      if (!restoreCart()) location.replace('/cart');
    });
    heading.append(close);
    section.prepend(heading);
    const sizePicker = section.querySelector('.product-details variant-picker');
    if (sizePicker && referenceProduct) {
      const style = document.createElement('div');
      style.className = 'hn-ui-cart-editor__style';
      style.innerHTML = '<span>Style</span><strong>Circle Acrylic Ornament</strong>';
      sizePicker.before(style);
    }
    const mountActions = () => {
      const buttons = section.querySelector('.product-form-buttons');
      if (!buttons || buttons.querySelector('.hn-ui-cart-editor__actions')) return false;
      const quantity = buttons.querySelector('input[name="quantity"]');
      if (quantity && Number(intent.originalQuantity) > 0) {
        quantity.value = String(intent.originalQuantity);
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const actions = document.createElement('div');
      actions.className = 'hn-ui-cart-editor__actions';
      actions.innerHTML = '<button class="hn-ui-cart-editor__save" type="button">Save changes</button><button class="hn-ui-cart-editor__add-new" type="button"><svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true"><path d="M2 3h3l2.2 11.2A3 3 0 0 0 10.1 17H19v-2h-8.9a1 1 0 0 1-1-.8L8.9 13h10.7a2 2 0 0 0 2-1.6L23 5H6.2L5.8 3H2Zm9 4h3V5h2v2h3v2h-3v3h-2V9h-3ZM10 18a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm9 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z"/></svg>Add as new item</button><p class="hn-ui-cart-editor__note"><span class="hn-ui-cart-editor__info" aria-hidden="true">i</span> Note: <strong>Want to keep both versions?</strong> Use “Add as new item”.</p><p class="hn-ui-cart-editor__error" role="alert" hidden></p>';
      buttons.append(actions);
      const save = actions.querySelector('.hn-ui-cart-editor__save');
      const addNew = actions.querySelector('.hn-ui-cart-editor__add-new');
      const error = actions.querySelector('.hn-ui-cart-editor__error');
      let running = false;
      const lockFields = (locked) => {
        for (const field of section.querySelectorAll('.customily_sets-panel, variant-picker, quantity-selector-component')) field.inert = locked;
        close.setAttribute('aria-disabled', String(locked));
      };
      save.disabled = true;
      addNew.disabled = true;
      actions.classList.add('is-busy');
      let preparingArtwork;
      const prepareArtwork = () => {
        document.dispatchEvent(new Event('hn-ui-cart-editor-prepare-artwork'));
        if (document.body.classList.contains('hn-ui-render-current')) return Promise.resolve();
        if (preparingArtwork) return preparingArtwork;
        preparingArtwork = (async () => {
          const preview = section.querySelector('.customily-preview-button');
          if (!preview) throw new Error('Design not ready');
          document.body.classList.add('hn-ui-cart-editor__preparing-preview');
          preview.click();
          const renderDeadline = Date.now() + 15000;
          while (!document.body.classList.contains('hn-ui-render-current') && Date.now() < renderDeadline) {
            await new Promise((resolve) => setTimeout(resolve, 100));
          }
          if (!document.body.classList.contains('hn-ui-render-current')) throw new Error('Design not ready');
          const modal = document.querySelector('.customily-modal-preview-only');
          if (modal && getComputedStyle(modal).display !== 'none') {
            modal.querySelector('.customily-close-button')?.click();
          }
        })().finally(() => {
          preparingArtwork = null;
          document.body.classList.remove('hn-ui-cart-editor__preparing-preview');
        });
        return preparingArtwork;
      };
      document.addEventListener('hn-ui-cart-edit-restore-start', () => {
        cartFieldsResolved = false; save.disabled = true; addNew.disabled = true;
        save.textContent = 'Save changes'; error.hidden = true; actions.classList.add('is-busy');
      });
      document.addEventListener('hn-ui-cart-edit-fields-resolved', () => {
        save.textContent = 'Save changes';
        error.hidden = true;
        save.disabled = false;
        addNew.disabled = false;
        actions.classList.remove('is-busy');
        syncImageState();
      });
      document.addEventListener('hn-ui-cart-edit-restore-error', (event) => {
        cartFieldsResolved = false;
        save.textContent = 'Try again';
        save.disabled = false;
        addNew.disabled = true;
        actions.classList.remove('is-busy');
        error.textContent = event.detail?.message || 'Your saved options could not be restored. Close and reopen the editor.';
        error.hidden = false;
        syncImageState();
      });
      document.addEventListener('hn-ui-cart-save-error', () => {
        running = false; save.disabled = false; addNew.disabled = true;
        close.setAttribute('aria-disabled', 'false');
        actions.classList.remove('is-busy');
        error.textContent = 'Could not finish saving. Please try Save changes again.';
        error.hidden = false;
      });
      const run = async (replace) => {
        if (running) return;
        if (!cartFieldsResolved) { document.dispatchEvent(new Event('hn-ui-cart-edit-retry')); return; }
        let nativeAdd = section.querySelector('#customily-cart-btn');
        running = true;
        error.hidden = true;
        save.disabled = true;
        addNew.disabled = true;
        actions.classList.add('is-busy');
        lockFields(true);
        try {
          if (replace && saveCommitted) { await window.HanossCompleteCartEdit(); return; }
          if (replace) {
            sessionStorage.removeItem('hn-hng04-cart-add-new-restore');
            const response = await fetch('/cart.js');
            if (!response.ok) throw new Error('Cart unavailable');
            const cart = await response.json();
            const original = cart.items.find((item) => item.key === intent.lineKey);
            if (!original) throw new Error('Cart item unavailable');
            const requestedQuantity = Math.max(1, Number(quantity?.value) || 1);
            const currentQuote = quoteValue();
            const currentRelationship = relationshipInput()?.value || '';
            const currentNames = crosswordInput()?.value || '';
            const currentText = customTextInput()?.value || '';
            const currentVariant = Number(section.querySelector('product-form-component input[name="id"]')?.value || 0);
            const unchanged = JSON.stringify(currentProperties()) === JSON.stringify(restoredProperties)
              && currentVariant === Number(original.variant_id);
            if (unchanged) {
              if (requestedQuantity !== original.quantity) {
                const update = await fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                  body: JSON.stringify({ id: original.key, quantity: requestedQuantity }),
                });
                if (!update.ok) throw new Error('Cart quantity unavailable');
              }
              sessionStorage.removeItem('hn-hng04-cart-legacy-replace');
              const fresh = await fetch('/cart.js').then((r) => r.json());
              await returnUpdatedCart(fresh);
              return;
            }
            sessionStorage.setItem('hn-hng04-cart-legacy-replace', JSON.stringify({
              lineKey: original.key,
              quantity: original.quantity,
              requestedQuantity,
              originalPersonalizationId: original.properties?.['_customily-personalization-id'] || '',
              expected: { variantId: currentVariant, productId: original.product_id, properties: currentProperties() },
              before: cart.items.map((item) => ({ key: item.key, quantity: item.quantity })),
              expires: Date.now() + 60 * 60 * 1000,
            }));
          } else {
            sessionStorage.removeItem('hn-hng04-cart-legacy-replace');
            const response = await fetch('/cart.js');
            if (!response.ok) throw new Error('Cart unavailable');
            const cart = await response.json();
            const original = cart.items.find((item) => item.key === intent.lineKey);
            if (!original) throw new Error('Cart item unavailable');
            sessionStorage.setItem('hn-hng04-cart-add-new-restore', JSON.stringify({
              original: { key: original.key, productId: original.product_id, variantId: original.variant_id, quantity: original.quantity, properties: original.properties },
              before: cart.items.map((item) => ({ key: item.key, quantity: item.quantity })),
              expires: Date.now() + 60 * 60 * 1000,
            }));
          }
          const readyAt = Date.now() + 15000;
          while ((!nativeAdd || nativeAdd.disabled) && Date.now() < readyAt) {
            await new Promise((resolve) => setTimeout(resolve, 200));
            nativeAdd = section.querySelector('#customily-cart-btn');
          }
          if (!nativeAdd || nativeAdd.disabled) throw new Error('Design not ready');
          await prepareArtwork();
          nativeAdd = section.querySelector('#customily-cart-btn');
          if (!nativeAdd || nativeAdd.disabled) throw new Error('Design not ready');
          nativeAdd.click();
          setTimeout(() => {
            if (saveCommitted) return;
            if (!running || !actions.isConnected) return;
            actions.classList.remove('is-busy');
            error.textContent = 'Your design is still processing. Close this editor and try again if it does not finish.';
            error.hidden = false;
          }, 30000);
        } catch (cause) {
          if (saveCommitted) return;
          lockFields(false);
          sessionStorage.removeItem('hn-hng04-cart-legacy-replace');
          sessionStorage.removeItem('hn-hng04-cart-add-new-restore');
          error.textContent = cause?.message === 'Design not ready'
            ? 'Could not refresh your design. Your original cart item is unchanged. Please try again.'
            : 'Could not prepare this cart item. Please try again.';
          error.hidden = false;
          running = false;
          save.disabled = false;
          addNew.disabled = false;
          actions.classList.remove('is-busy');
        }
      };
      save.addEventListener('click', () => run(true));
      addNew.addEventListener('click', () => run(false));
      document.addEventListener('shopify:cart:lines-update', (event) => {
        if (event.action !== 'add' || !event.promise) return;
        event.promise.then(({ detail }) => {
          if (!detail?.didError) return;
          lockFields(false);
          running = false;
          save.disabled = false;
          addNew.disabled = false;
          actions.classList.remove('is-busy');
          error.textContent = 'Could not update your cart. Please try again.';
          error.hidden = false;
        }).catch(() => {
          if (saveCommitted) return;
          lockFields(false);
          running = false;
          save.disabled = false;
          addNew.disabled = false;
          actions.classList.remove('is-busy');
        });
      }, { capture: true });
      return true;
    };
    if (!mountActions()) {
      const observer = new MutationObserver(() => { if (mountActions()) observer.disconnect(); });
      observer.observe(section, { childList: true, subtree: true });
    }
  };
  const setupGiftAccordions = () => {
    const menu = document.querySelector('#link-gifts .menu-drawer__menu--childlist');
    if (!menu || menu.dataset.hnUiReady) return;
    menu.dataset.hnUiReady = 'true';

    const makeToggle = (title) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hn-ui-group__toggle';
      button.textContent = title;
      button.setAttribute('aria-expanded', 'false');
      button.addEventListener('click', () => {
        const opening = button.getAttribute('aria-expanded') !== 'true';
        menu.querySelectorAll('.hn-ui-group__toggle[aria-expanded="true"]').forEach((other) => {
          other.setAttribute('aria-expanded', 'false');
        });
        button.setAttribute('aria-expanded', String(opening));
      });
      return button;
    };
    const makeExtra = (title, links) => {
      const item = document.createElement('li');
      item.className = 'menu-drawer__list-item hn-ui-group';
      item.append(makeToggle(title));
      const list = document.createElement('ul');
      list.className = 'menu-drawer__menu menu-drawer__menu--grandchildlist';
      for (const [label, href] of links) {
        const leaf = document.createElement('li');
        const link = document.createElement('a');
        link.href = href;
        link.textContent = label;
        leaf.append(link);
        list.append(leaf);
      }
      item.append(list);
      return item;
    };

    for (const item of Array.from(menu.children)) {
      const parent = item.querySelector(':scope > a.menu-drawer__menu-item--parent');
      if (!parent) continue;
      const title = parent.textContent.trim();
      if (title === 'Discover') {
        item.hidden = true; // These links remain in the main menu; keep this submenu reference-shaped.
        continue;
      }
      item.classList.add('hn-ui-group');
      item.insertBefore(makeToggle(title), parent);
      if (title === 'By Product') {
        const interestUrl = document.querySelector('.hn-mobile-menu-shell')?.dataset.hnInterestUrl || '/collections/all';
        item.before(makeExtra('By Interest', [['Browse all gifts', interestUrl]]));
        item.after(makeExtra('By Price', [
          ['Price: Low to High', '/collections/all?sort_by=price-ascending'],
          ['Price: High to Low', '/collections/all?sort_by=price-descending'],
        ]));
      }
    }
  };
  const decorateRelationshipSwatches = () => {
    const namesOption = customilyOptionWithFunction('crossword');
    const textOption = customTextOption();
    if (namesOption && textOption && namesOption.parentElement === textOption.parentElement && textOption.nextElementSibling !== namesOption) {
      namesOption.before(textOption);
    }
    const namesLabel = namesOption?.querySelector('.option_name > div');
    const configuredLabel = window.HanossCustomilyCapabilities?.getLabel?.(namesOption);
    namesLabel?.childNodes.forEach((node) => {
      if (configuredLabel && node.nodeType === Node.TEXT_NODE && node.textContent.includes(configuredLabel)) {
        node.textContent = node.textContent.replace(configuredLabel, 'Enter Names For Crossword (Add up to 15 Names)');
      }
    });
    window.HanossCustomilyChoiceControls?.mount();
  };
  const setupQuoteKeyboard = () => {
    window.HanossCustomilyChoiceControls?.init();
  };
  const setupCustomilyValidation = () => {
    for (const { key, option, message, controlSelector } of [
      {
        key: 'quote',
        option: quoteOption(),
        message: `${window.HanossCustomilyCapabilities?.getLabel?.(quoteOption()) || 'Selection'} is required`,
        controlSelector: '.csh-dropdown-container',
      },
      { key: 'crossword', option: crosswordOptionElement(), message: 'Enter Names For Crossword is required', controlSelector: '.hn-ui-name-tags' },
    ]) {
      const required = option?.querySelector('.customily-required-label > span');
      const control = option?.querySelector(controlSelector);
      if (!required || !control) continue;
      let alert = option.querySelector('.hn-ui-validation-message');
      if (!alert) {
        alert = document.createElement('p');
        alert.id = `hn-ui-validation-${key}`;
        alert.className = 'hn-ui-validation-message';
        alert.setAttribute('role', 'alert');
        alert.textContent = message;
        alert.hidden = true;
        control.after(alert);
      }
      const sync = () => {
        const value = window.HanossCustomilyCapabilities?.getPropertyControl?.(option, { checked: true })?.value.trim();
        if (value) delete option.dataset.hnUiManualRequired;
        const specificError = key === 'crossword' ? option.querySelector('.hn-ui-name-tags__error:not([hidden])') : null;
        const hasSpecificError = Boolean(specificError?.textContent.trim());
        const missing = (required.style.display !== 'none' || option.dataset.hnUiManualRequired === 'true') && !value && !hasSpecificError;
        const invalid = missing || hasSpecificError;
        option.classList.toggle('hn-ui-invalid', invalid);
        alert.hidden = !missing;
        required.setAttribute('aria-hidden', 'true');
        const input = option.querySelector(key === 'quote' ? '.csh-dropdown-input-container' : '.hn-ui-name-tags__entry');
        input?.setAttribute('aria-invalid', String(invalid));
        if (hasSpecificError) input?.setAttribute('aria-describedby', specificError.id);
        else if (missing) input?.setAttribute('aria-describedby', alert.id);
        else input?.removeAttribute('aria-describedby');
      };
      if (!required.dataset.hnUiValidationReady) {
        required.dataset.hnUiValidationReady = 'true';
        new MutationObserver(sync).observe(required, { attributes: true, attributeFilter: ['style'] });
        option.addEventListener('click', () => requestAnimationFrame(sync));
        option.addEventListener('input', sync);
        option.addEventListener('change', sync);
        const specificError = key === 'crossword' ? option.querySelector('.hn-ui-name-tags__error') : null;
        if (specificError) new MutationObserver(sync).observe(specificError, { attributes: true, attributeFilter: ['hidden'], childList: true });
      }
      sync();
    }
  };
  const setupNameTags = () => {
    window.HanossCustomilyNameTags?.init();
    setupQuoteKeyboard();
    setupCustomilyValidation();
    decorateRelationshipSwatches();
    if (document.body.dataset.hnUiTagObserverReady) return;
    document.body.dataset.hnUiTagObserverReady = 'true';
    window.HanossCustomilyCapabilities?.onPanelChange(() => {
      window.HanossCustomilyNameTags?.mount();
      setupQuoteKeyboard();
      setupCustomilyValidation();
      decorateRelationshipSwatches();
    });
  };
  const setupPersonalizationPersistence = () => {
    if (document.body.dataset.hnUiPersistenceReady) return;
    let cartEditIntent;
    try { cartEditIntent = JSON.parse(sessionStorage.getItem('hn-hng04-cart-edit-intent') || 'null'); } catch (_) {}
    const editingCartItem = document.body.classList.contains('hn-ui-cart-editor');
    document.body.dataset.hnUiPersistenceReady = 'true';
    const key = 'hn-hng04-personalization-v1';
    let restoring = true;
    let saveTimer;
    const capture = () => {
      if (restoring || editingCartItem || !quoteInput()) return;
      const state = {
        savedAt: Date.now(),
        quote: quoteValue(),
        relationship: relationshipInput()?.value || '',
        names: crosswordInput()?.value.trim() || '',
        customText: customTextInput()?.value.trim() || '',
      };
      if (!state.quote && !state.relationship && !state.names && !state.customText) {
        try { localStorage.removeItem(key); } catch (_) {}
        return;
      }
      try { localStorage.setItem(key, JSON.stringify(state)); } catch (_) {}
    };
    const scheduleCapture = () => {
      if (restoring) return;
      clearTimeout(saveTimer);
      saveTimer = setTimeout(capture, 250);
    };
    for (const eventName of ['input', 'change', 'click']) {
      document.addEventListener(eventName, (event) => {
        if (!event.target?.closest?.('.customily_sets-panel, .hn-ui-name-tags')) return;
        if (!restoring && (eventName === 'input' || event.target.closest('.csh-dropdown-item') || relationshipOption()?.contains(event.target))) {
          document.body.classList.remove('hn-ui-restored-personalization');
        }
        scheduleCapture();
      }, { capture: true });
    }
    const controller = new AbortController();
    const attempt = {};
    let closed = false, succeeded = false;
    if (editingCartItem) window.HanossCartRestoreAttempt = attempt;
    const active = () => !closed && (!editingCartItem || window.HanossCartRestoreAttempt === attempt);
    const lockOptions = (locked) => {
      if (editingCartItem) for (const field of document.querySelectorAll('.customily_sets-panel, variant-picker, quantity-selector-component')) field.inert = locked;
    };
    lockOptions(true);
    const releasePanelLock = editingCartItem
      ? window.HanossCustomilyCapabilities?.onPanelChange(() => { if (active() && !succeeded) lockOptions(true); })
      : null;
    const closeRestore = () => {
      closed = true; releasePanelLock?.();
      controller.abort(new Error('Editor closed.'));
      document.removeEventListener('hn-ui-cart-edit-retry', retryRestore);
    };
    const retryRestore = () => {
      if (!active() || restoring) return;
      document.removeEventListener('hn-ui-cart-edit-retry', retryRestore);
      releasePanelLock?.();
      document.removeEventListener('hn-ui-cart-editor-closed', closeRestore);
      delete document.body.dataset.hnUiPersistenceReady;
      setupPersonalizationPersistence();
    };
    document.addEventListener('hn-ui-cart-editor-closed', closeRestore, { once: true });
    if (editingCartItem) {
      document.addEventListener('hn-ui-cart-edit-retry', retryRestore);
      document.dispatchEvent(new Event('hn-ui-cart-edit-restore-start'));
    }
    const cancel = (event) => {
      if (!editingCartItem && restoring && event.isTrusted && event.target?.closest?.('.customily_sets-panel, .hn-ui-name-tags, variant-picker')) controller.abort(new Error('Draft restore interrupted by input.'));
    };
    const cancelEvents = ['input', 'change'];
    cancelEvents.forEach((name) => document.addEventListener(name, cancel, { capture: true }));
    const restore = async () => {
      const api = window.HanossCustomilyCapabilities;
      if (!api?.waitForState) throw new Error('Customily restore is unsupported.');
      const deadline = Date.now() + 60000;
      const wait = (predicate) => api.waitForState(predicate, { signal: controller.signal, timeout: Math.max(1, deadline - Date.now()) });
      const assertActive = () => { if (controller.signal.aborted) throw controller.signal.reason; if (!active()) throw new Error('Restore superseded.'); };
      const intent = cartEditIntent;
      if (editingCartItem && (!intent || intent.expires < Date.now() || !matchesCartEditLocation(intent, location))) {
        throw new Error('The saved cart item is no longer available. Close and reopen the editor.');
      }
      let draft;
      if (!editingCartItem) {
        try { draft = JSON.parse(localStorage.getItem(key) || 'null'); } catch {}
        if (!draft || Date.now() - draft.savedAt > 7 * 86400000) return;
      }
      // Variant is selected by the cart URL before provider initialization.
      // Do not apply option values to the previous variant's template.
      if (editingCartItem) await wait(() => Number(document.querySelector('product-form-component input[name="id"]')?.value) === Number(intent.originalVariantId)
        && Number(document.querySelector('.product-form-buttons input[name="quantity"]')?.value) === Number(intent.originalQuantity || 1));
      await wait(api.providerReady);
      let properties = intent?.properties || {};
      if (!editingCartItem) {
        properties = {};
        for (const [field, control] of Object.entries({ quote: quoteInput(), relationship: relationshipInput(), names: crosswordInput(), customText: customTextInput() })) {
          if (control && draft[field] != null) properties[propertyKey(control)] = draft[field];
        }
      }
      const stored = editingCartItem && !intent.legacy ? await loadStoredCustomization(intent) : null;
      assertActive();
      let attempts = 0;
      while (attempts++ < 2) {
        await wait(api.providerReady);
        assertActive();
        const instance = api.getOptionsInstance();
        const sets = instance.sets;
        const items = api.restoreItems(properties, stored);
        if (!items.length) throw new Error('No saved options could be mapped to this product configuration.');
        const different = items.filter((item) => {
          const model = api.modelForItem(item);
          return !api.sameValue(model?.type === 'Color Picker' ? model.optionValue : model?.value, item.value);
        });
        if (different.length) {
          const result = await api.applyOptionValues(different);
          if (result.status === 'unsupported') throw new Error('Customily restore is unsupported.');
        }
        assertActive();
        if (api.getOptionsInstance() !== instance || instance.sets !== sets) continue;
        // The provider defers text writes while restoring. Verify real Fabric
        // text, and enqueue the normal provider change only when it is missing.
        if (!api.canvasTextsMatch(items)) {
          for (const { model, setId } of api.textExpectations(items)) {
            assertActive(); instance.optionChange(model, setId);
          }
          await instance.engraverDispatcher.commit();
        }
        await instance.$nextTick();
        assertActive();
        window.HanossCustomilyNameTags?.refresh();
        const crosswords = items.filter((item) => api.modelForItem(item)?.functions?.some((fn) => fn.type === 'crossword'));
        if (crosswords.length) {
          document.dispatchEvent(new Event('hn-ui-cart-editor-prepare-artwork'));
          await wait(() => window.HanossCustomilyCrosswordPreview?.mounted);
          assertActive();
          await window.HanossCustomilyCrosswordPreview.requestCurrentRender();
        }
        const rebuilt = () => api.getOptionsInstance() !== instance || instance.sets !== sets;
        await wait(() => rebuilt() || (api.verifyOptionValues(items) && api.canvasTextsMatch(items)
          && (!crosswords.length || (window.HanossCustomilyNameTags?.matches() && crosswords.every((item) => {
            const model = api.modelForItem(item);
            return model?.lastGeneratedValue === item.value && model.loadingFunction === false && model.errorGeneratingFunction === false;
          }) && document.body.classList.contains('hn-ui-render-current')))));
        if (rebuilt()) continue;
        // A finite settling window catches provider sets replacement, then all
        // restore subscriptions are removed. Never replay after a user edit.
        await new Promise((resolve) => setTimeout(resolve, 1000));
        assertActive();
        if (rebuilt()) continue;
        if (!api.verifyOptionValues(items) || !api.canvasTextsMatch(items)) throw new Error('Customily reset the saved values during initialization.');
        succeeded = true; lockOptions(false);
        document.body.classList.add('hn-ui-restored-personalization');
        if (editingCartItem) {
          const fields = { quote: quoteValue(), relationship: relationshipInput()?.value || '', names: crosswordInput()?.value || '', customText: customTextInput()?.value || '' };
          Object.assign(intent, fields);
          sessionStorage.setItem('hn-hng04-cart-edit-intent', JSON.stringify(intent));
          document.dispatchEvent(new CustomEvent('hn-ui-cart-edit-fields-resolved', { detail: fields }));
        }
        return;
      }
      throw new Error('Customily repeatedly rebuilt the saved options. Close this editor and try again.');
    };
    let restoreTimer;
    const boundedRestore = Promise.race([
      restore(),
      new Promise((_, reject) => {
        restoreTimer = setTimeout(() => {
          const error = new Error(window.HanossCustomilyCapabilities?.getOptionsInstance()
            ? 'Your saved options or artwork could not be restored in time. Please try again.'
            : 'This Customily version does not support restoring saved options (unsupported).');
          controller.abort(error);
          reject(error);
        }, 60000);
      }),
    ]);
    boundedRestore.catch((error) => {
      if (editingCartItem && active()) document.dispatchEvent(new CustomEvent('hn-ui-cart-edit-restore-error', { detail: { message: error.message } }));
    }).finally(() => {
      clearTimeout(restoreTimer);
      restoring = false;
      if (succeeded || !active()) {
        releasePanelLock?.();
        document.removeEventListener('hn-ui-cart-editor-closed', closeRestore);
        document.removeEventListener('hn-ui-cart-edit-retry', retryRestore);
      }
      cancelEvents.forEach((name) => document.removeEventListener(name, cancel, { capture: true }));
      capture();
    });
  };

  const setupDeliveryDialog = () => {
    if (document.body.dataset.hnUiDeliveryReady) return;
    document.body.dataset.hnUiDeliveryReady = 'true';
    const dialog = document.querySelector('#hn-ui-delivery-dialog');
    if (!dialog) return;
    let lastTrigger;
    let closing = false;
    let closeTimer;
    const closeDialog = () => {
      if (!dialog.open || closing) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        dialog.close();
        return;
      }
      closing = true;
      dialog.classList.add('is-closing');
      closeTimer = window.setTimeout(() => dialog.close(), 400);
    };
    document.addEventListener('click', (event) => {
      const trigger = event.target?.closest?.('.hn-eta__delivery-button');
      if (!trigger) return;
      event.preventDefault();
      lastTrigger = trigger;
      if (!dialog.open) {
        clearTimeout(closeTimer);
        closing = false;
        dialog.classList.remove('is-closing');
        dialog.classList.add('is-entering');
        dialog.showModal();
        requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.remove('is-entering')));
      }
      if (event.detail === 0) dialog.querySelector('.hn-ui-delivery-dialog__close')?.focus();
      else dialog.focus({ preventScroll: true });
    });
    document.addEventListener('click', (event) => {
      if (event.target?.matches?.('.hn-ui-delivery-dialog__close')) closeDialog();
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDialog();
    });
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    document.addEventListener('close', (event) => {
      if (event.target !== dialog) return;
      clearTimeout(closeTimer);
      closing = false;
      dialog.classList.remove('is-entering', 'is-closing');
      lastTrigger?.focus();
    }, { capture: true });
  };
  const setupRecommendationsHeading = () => {
    const root = document.querySelector('product-recommendations');
    if (!root) return;
    const sync = () => {
      const heading = root.querySelector('.section-resource-list__content h3');
      if (!heading || heading.textContent.trim() !== 'You may also like') return;
      const replacement = document.createElement('h2');
      replacement.className = 'hn-ui-recommendations-title';
      replacement.textContent = 'More Items to Consider';
      heading.replaceWith(replacement);
    };
    sync();
    new MutationObserver(sync).observe(root, { childList: true, subtree: true });
  };
  const setupBackToTop = () => {
    if (document.querySelector('.hn-ui-back-to-top')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hn-ui-back-to-top';
    button.setAttribute('aria-label', 'Scroll to top');
    button.innerHTML = '<span aria-hidden="true"></span>';
    document.body.append(button);
    const page = document.querySelector('.page-wrapper');
    const scrollContainer = () => page && page.scrollHeight > page.clientHeight + 1 && getComputedStyle(page).overflowY !== 'visible'
      ? page : document.scrollingElement;
    let lastTop = scrollContainer()?.scrollTop || 0;
    const onScroll = () => {
      const top = scrollContainer()?.scrollTop || 0;
      if (top < 250) button.classList.remove('is-visible');
      else if (top < lastTop - 2) button.classList.add('is-visible');
      else if (top > lastTop + 2) button.classList.remove('is-visible');
      lastTop = top;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    page?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      lastTop = scrollContainer()?.scrollTop || 0;
      button.classList.remove('is-visible');
    });
    button.addEventListener('click', () => {
      button.classList.remove('is-visible');
      scrollContainer()?.scrollTo({ top: 0, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    });
  };
  const init = () => {
    setupCartEditPage();
    if (!document.body.classList.contains('hn-ui-reference')) return;
    setupGiftAccordions();
    setupNameTags();
    setupPersonalizationPersistence();
    setupQuoteKeyboard();
    setupCustomilyValidation();
    decorateRelationshipSwatches();
    window.addEventListener('resize', decorateRelationshipSwatches);
    setupDeliveryDialog();
    // Warm only an already uploaded image when Customily writes cart properties.
    // No export/upload is started while the shopper edits their design.
    const cartForm = document.querySelector('product-form-component form[action*="/cart/add"]');
    if (cartForm && !cartForm.dataset.hnCartImageWarm) {
      cartForm.dataset.hnCartImageWarm = 'true';
      let lastSource = '';
      let warmImage;
      const warmCartImage = () => {
        const source = cartForm.querySelector('[name="properties[_customily-thumb]"]')?.value
          || cartForm.querySelector('[name="properties[_customily-preview]"]')?.value;
        if (!source || source === lastSource) return;
        let url;
        try { url = new URL(source); } catch { return; }
        if (url.protocol !== 'https:' || url.hostname !== 'cdn.customily.com') return;
        lastSource = source;
        warmImage = new Image();
        warmImage.decoding = 'async';
        warmImage.fetchPriority = 'high';
        warmImage.src = source;
      };
      new MutationObserver(warmCartImage).observe(cartForm, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['value'],
      });
      cartForm.addEventListener('submit', warmCartImage, { capture: true });
    }
    setupRecommendationsHeading();
    setupBackToTop();

    // The reference completes a successful add on the full cart page. Keep the
    // theme's own async form, validation and cart-line payload; navigate only
    // after its authoritative cart promise succeeds.
    // Capture before the theme's document-level drawer listener. Only an HNG-04
    // add suppresses drawer auto-open; quick-adds of other products stay native.
    document.addEventListener('click', (event) => {
      if (event.target?.closest?.('.hn-mobile-menu-utility--bottom a[href="#judgeme_product_reviews"]')) {
        document.querySelector('.hn-mobile-menu-shell .menu-drawer__close-button')?.click();
      }
    });
    document.addEventListener('shopify:cart:lines-update', (event) => {
      if (event.action !== 'add' || !event.promise) return;
      const form = event.target instanceof Element ? event.target.closest('product-form-component') : null;
      if (!form || !crosswordOptionElement()) return;
      event.promise.then(({ detail }) => {
        if (detail?.didError) return;
        const completion = window.HanossCompleteCartEdit?.();
        if (completion) return completion;
        try { sessionStorage.removeItem('hn-hng04-cart-edit-intent'); } catch (_) {}
        sessionStorage.removeItem('hn-hng04-cart-edit-background');
        sessionStorage.removeItem('hn-hng04-cart-return');
        window.location.assign('/cart');
      }).catch(() => {});
    }, { capture: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', () => {
    setupGiftAccordions();
    setupNameTags();
    decorateRelationshipSwatches();
    setupDeliveryDialog();
  });
  document.addEventListener('click', (event) => {
    if (event.target?.closest?.('summary[aria-label="Menu"]')) queueMicrotask(setupGiftAccordions);
  });
  document.addEventListener('click', (event) => {
    if (!event.target?.closest?.('body.hn-ui-reference .product-information__media media-gallery slideshow-slide')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, { capture: true });
})();
